import React, { useEffect, useState } from "react";
import "./aiAssignmentPanel.css";
import { useMutation } from "@apollo/client";
import {
  GENERATE_ASSIGNMENT_SUGGESTIONS,
  ACCEPT_DAY_ASSIGNMENTS,
} from "../../../../utils/GraphQL/mutations";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";

const AIAssignmentPanel = ({ startMs, endMs, showDatePickers, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [localStartMs, setLocalStartMs] = useState(startMs || "");
  const [localEndMs, setLocalEndMs] = useState(endMs || "");
  const [suggestions, setSuggestions] = useState(null);
  const [acceptedDays, setAcceptedDays] = useState({});
  const [error, setError] = useState(null);

  const { handleSnack } = useSnackbar();

  const [generateSuggestions] = useMutation(GENERATE_ASSIGNMENT_SUGGESTIONS);
  const [acceptDay] = useMutation(ACCEPT_DAY_ASSIGNMENTS);

  // On mount: compute current week (Sun-Sat) if no props provided
  useEffect(() => {
    if (!startMs && !endMs) {
      const now = new Date();
      const sun = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - now.getDay()
      );
      const sat = new Date(
        sun.getFullYear(),
        sun.getMonth(),
        sun.getDate() + 6,
        23,
        59,
        59,
        999
      );
      setLocalStartMs(sun.getTime().toString());
      setLocalEndMs(sat.getTime().toString());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset suggestions/acceptedDays when startMs/endMs props change
  useEffect(() => {
    if (startMs) setLocalStartMs(startMs);
    if (endMs) setLocalEndMs(endMs);
    setSuggestions(null);
    setAcceptedDays({});
  }, [startMs, endMs]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await generateSuggestions({
        variables: {
          input: { startMs: localStartMs, endMs: localEndMs },
        },
      });
      setSuggestions(data.generateAssignmentSuggestions);
      setAcceptedDays({});
    } catch (err) {
      const msg = err.message || "Failed to generate suggestions";
      setError(msg);
      handleSnack(msg, 5000);
    }
    setLoading(false);
  };

  const handleAcceptDay = async (dayObj) => {
    const assignments = dayObj.assignments.map((a) => ({
      calendarItemId: a.calendarItemId,
      assignedRefinisherIds: a.assignedRefinisherIds,
    }));
    try {
      await acceptDay({
        variables: { input: { assignments } },
      });
      setAcceptedDays((prev) => ({ ...prev, [dayObj.date]: true }));
    } catch (err) {
      handleSnack(err.message || "Failed to accept day", 5000);
    }
  };

  const handleAcceptAll = async () => {
    if (!suggestions?.days) return;
    for (const day of suggestions.days) {
      if (!acceptedDays[day.date] && day.assignments.length > 0) {
        await handleAcceptDay(day);
      }
    }
  };

  // Build refinisher name map
  const nameMap = {};
  if (suggestions?.refinisherSummaries) {
    suggestions.refinisherSummaries.forEach((rs) => {
      nameMap[rs.refinisherId] = rs.refinisherName;
    });
  }

  const formatDayLabel = (dateStr) => {
    const dt = new Date(dateStr + "T12:00:00");
    return dt.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="ai-panel">
      {/* Header */}
      <div className="ai-panel-header">
        <span className="ai-panel-title">AI Job Assignment</span>
        <button className="ai-panel-close" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Date pickers (only if showDatePickers) */}
      {showDatePickers && (
        <div className="ai-panel-date-row">
          <EventDateTimeInput
            value={localStartMs}
            onChange={(ms) => {
              setLocalStartMs(ms);
              setSuggestions(null);
              setAcceptedDays({});
            }}
            label="Start:"
            hideTime={true}
          />
          <EventDateTimeInput
            value={localEndMs}
            onChange={(ms) => {
              setLocalEndMs(ms);
              setSuggestions(null);
              setAcceptedDays({});
            }}
            label="End:"
            hideTime={true}
          />
        </div>
      )}

      {/* Generate button */}
      <div className="ai-panel-gen-row">
        <button
          className="ai-panel-gen-btn"
          onClick={handleGenerate}
          disabled={loading}
        >
          {loading
            ? "Generating..."
            : suggestions
            ? "Regenerate"
            : "Generate Suggestions"}
        </button>
      </div>

      {/* Loading bar */}
      {loading && (
        <div className="ai-panel-loading">
          <div className="ai-panel-loading-bar" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="ai-panel-error">
          <span>⚠ {error}</span>
        </div>
      )}

      {/* Results */}
      {suggestions && !loading && (
        <div className="ai-panel-results">
          {/* Day cards */}
          {suggestions.days.map((day) => {
            const isAccepted = !!acceptedDays[day.date];
            return (
              <div
                key={day.date}
                className={`ai-panel-day-card ${isAccepted ? "accepted" : ""}`}
              >
                <div className="ai-panel-day-header">
                  <span className="ai-panel-day-label">
                    {formatDayLabel(day.date)}
                  </span>
                  {isAccepted ? (
                    <span className="ai-panel-accepted-badge">✓ Accepted</span>
                  ) : (
                    <button
                      className="ai-panel-accept-btn"
                      onClick={() => handleAcceptDay(day)}
                      disabled={day.assignments.length === 0}
                    >
                      Accept Day
                    </button>
                  )}
                </div>
                {day.assignments.length === 0 ? (
                  <div className="ai-panel-no-jobs">No jobs scheduled</div>
                ) : (
                  <table className="ai-panel-table">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th>Assigned</th>
                        <th>Split</th>
                      </tr>
                    </thead>
                    <tbody>
                      {day.assignments.map((a) => (
                        <tr key={a.calendarItemId}>
                          <td>
                            {a.locked && <span title="Locked" style={{ marginRight: 4, fontSize: 12 }}>🔒</span>}
                            {a.customerName || a.jobAddress || "N/A"}
                          </td>
                          <td>
                            {a.assignedRefinisherIds
                              .map((id) => nameMap[id] || id.slice(-4))
                              .join(", ")}
                          </td>
                          <td>
                            {a.splitCount > 1 ? `${a.splitCount}-way` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}

          {/* Weekly Summary */}
          {suggestions.refinisherSummaries.length > 0 && (
            <div className="ai-panel-summary">
              <div className="ai-panel-summary-title">Weekly Summary</div>
              <table className="ai-panel-table">
                <thead>
                  <tr>
                    <th>Refinisher</th>
                    <th>Sen.</th>
                    <th>Pay %</th>
                    <th>Net Pay</th>
                    <th>Jobs</th>
                    <th>Drive</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.refinisherSummaries.map((rs) => {
                    const totalJobs = rs.dailyBreakdown.reduce(
                      (s, d) => s + d.jobCount,
                      0
                    );
                    const totalDrive = rs.dailyBreakdown.reduce(
                      (s, d) => s + (d.totalDriveMinutes || 0),
                      0
                    );
                    return (
                      <tr key={rs.refinisherId}>
                        <td>{rs.refinisherName}</td>
                        <td>{rs.seniority || "—"}</td>
                        <td>{rs.payPercentage || "—"}</td>
                        <td>${rs.weeklyNetPay.toFixed(2)}</td>
                        <td>{totalJobs}</td>
                        <td>{totalDrive > 0 ? `${totalDrive}m` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Warnings */}
          {suggestions.warnings?.length > 0 && (
            <div className="ai-panel-warnings">
              <div className="ai-panel-warnings-title">⚠ Warnings</div>
              {suggestions.warnings.map((w, i) => (
                <div key={i} className="ai-panel-warning-item">
                  {w}
                </div>
              ))}
            </div>
          )}

          {/* Accept All */}
          {suggestions.days.length > 0 &&
            !suggestions.days.every((d) => acceptedDays[d.date]) && (
              <button
                className="ai-panel-accept-all-btn"
                onClick={handleAcceptAll}
              >
                Accept All Days
              </button>
            )}
        </div>
      )}
    </div>
  );
};

export default AIAssignmentPanel;
